import { Button, Image, Text } from "@fluentui/react-components";
import { MicOffRegular, MicRegular } from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAutosizeTextarea } from "../../useAutosizeTextArea";
import { useAzureSpeech } from "../useAzureSpeech/useAzureSpeech";
import { VOICE_TEXT } from "./voiceControls.constants";
import { useStyles } from "./voiceControls.styles";
import {
  IVoiceControlsProps,
  VoiceControlsHandle,
} from "./voiceControls.types";

export const VoiceControls = forwardRef<
  VoiceControlsHandle,
  IVoiceControlsProps
>((props, ref) => {
  const {
    diagnosticId,
    distributionId,
    enabled,
    locale,
    voiceName,
    micDeviceId,
    questionText,
    answer,
    setAnswer,
    isValid,
    setErrorText,
    onSubmit,
    onListeningStart,
    onAskingStart,
    onVoiceStateUpdate,
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const playQuestionAgainInProgressRef = useRef(false);
  const isPausedRef = useRef(false);

  const handleSilenceDetected = useCallback(
    async (finalText: string) => {
      if (isPausedRef.current || isSubmitting) return;

      if (!isValid(finalText)) {
        setErrorText(VOICE_TEXT.tooShort);
        return;
      }

      setIsSubmitting(true);
      speech.stop();
      await onSubmit(finalText);
      setIsSubmitting(false);
      setAnswer(ConstantValues.EMPTY_STRING);
      setErrorText(ConstantValues.EMPTY_STRING);
    },
    [isValid, onSubmit, setAnswer, setErrorText, enabled, questionText],
  );

  const speech = useAzureSpeech({
    diagnosticId,
    distributionId,
    enabled,
    locale,
    voiceName,
    deviceId: micDeviceId,
    onSilenceDetected: handleSilenceDetected,
  });
  const styles = useStyles();

  useImperativeHandle(
    ref,
    () => ({
      pause: () => {
        isPausedRef.current = true;
        speech.pauseSpeaking();
        speech.stop();
      },
      resume: () => {
        isPausedRef.current = false;
        speech.start();
      },
      playQuestionAgain: async () => {
        if (!questionText || !speech.ready) return;
        isPausedRef.current = false;
        playQuestionAgainInProgressRef.current = true;
        try {
          speech.stop();
          onAskingStart?.();
          await speech.speak(questionText);
          if (isPausedRef.current) return;
          speech.start();
          onListeningStart?.();
        } finally {
          playQuestionAgainInProgressRef.current = false;
        }
      },
    }),
    [
      speech.pauseSpeaking,
      speech.stop,
      speech.start,
      speech.speak,
      speech.ready,
      speech.listening,
      questionText,
      onListeningStart,
      onAskingStart,
    ],
  );

  const valueToShow = speech.listening
    ? speech.partial || speech.transcript || answer
    : answer;
  const { onInput } = useAutosizeTextarea(valueToShow, {
    maxHeight: "40vh",
  });

  useEffect(() => {
    if (!enabled || !speech.ready || !questionText || isSubmitting) return;

    const startConversation = async () => {
      isPausedRef.current = false;
      if (playQuestionAgainInProgressRef.current) return;
      if (
        enabled &&
        speech.ready &&
        !speech.listening &&
        !speech.speaking &&
        !isSubmitting
      ) {
        try {
          onAskingStart?.();
          await speech.speak(questionText);
          if (isPausedRef.current) return;
          if (!playQuestionAgainInProgressRef.current) {
            if (enabled && speech.ready && !speech.listening && !isSubmitting) {
              speech.start();
            }
            onListeningStart?.();
          }
        } catch (err) {
          console.log("Speech interrupted or completed", err);
        }
      }
    };

    startConversation();
    return;
  }, [
    enabled,
    speech.ready,
    questionText,
    isSubmitting,
    onListeningStart,
    onAskingStart,
  ]);

  useEffect(() => {
    if (speech.speaking) {
      onAskingStart?.();
    }
  }, [speech.speaking, onAskingStart]);

  useEffect(() => {
    if (answer.length === 0) {
      onInput();
    }
  }, [answer, onInput]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const currentText = speech.transcript || ConstantValues.EMPTY_STRING;
    if (currentText) {
      setAnswer(currentText);
      setErrorText(
        isValid(currentText)
          ? ConstantValues.EMPTY_STRING
          : VOICE_TEXT.tooShort,
      );
    }
  }, [enabled, speech.transcript, setAnswer, isValid, setErrorText]);

  useEffect(() => {
    onVoiceStateUpdate?.(speech.isDetectingSpeech, speech.silenceTimer);
  }, [speech.isDetectingSpeech, speech.silenceTimer, onVoiceStateUpdate]);

  useEffect(() => {
    return () => {
      console.log("VoiceControls unmounting - disposing speech");
      speech.dispose();
    };
  }, [speech.dispose]);

  const statusText = useMemo(() => {
    if (isSubmitting) {
      return "Submitting Answer...";
    }
    if (!enabled) {
      return "Voice disabled";
    }

    if (!speech.ready) {
      return "Initializing voice...";
    }

    if (speech.speaking) {
      return "Asking question...";
    }

    if (speech.listening) {
      if (speech.speaking) {
        return "Asking Question...";
      }

      if (speech.isDetectingSpeech) {
        return "Listening... (speech detected)";
      }
      return "Listening...";
    }

    return "Ready";
  }, [
    enabled,
    speech.ready,
    speech.listening,
    speech.speaking,
    speech.isDetectingSpeech,
    speech.silenceTimer,
    isSubmitting,
  ]);

  return (
    <div className={styles.grid}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "16px",
        }}
      >
        <Image
          alt="Assistant avatar"
          shape="circular"
          src="/images/NH_Logo_Circle.png"
          height={80}
          width={80}
        />
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            {speech.isDetectingSpeech ? <MicRegular /> : <MicOffRegular />}
            <Text weight="semibold" size={300}>
              {statusText}
            </Text>
          </div>

          {speech.listening && (
            <Text
              size={200}
              style={{ textAlign: "center", marginTop: "8px", opacity: 0.7 }}
            >
              {speech.isDetectingSpeech
                ? "Keep speaking... Message will auto-send after 3 seconds of silence."
                : speech.speaking
                  ? "AI is speaking"
                  : "Start speaking anytime..."}
            </Text>
          )}
        </div>
      </div>

      {speech.audioElement}

      <div className={styles.row}>
        {speech.speaking ? (
          <Button
            shape="circular"
            appearance="secondary"
            onClick={() => speech.pauseSpeaking()}
            disabled={!speech.speaking || !speech.ready}
          >
            {" "}
            Stop Question{" "}
          </Button>
        ) : (
          <Button
            shape="circular"
            appearance="outline"
            onClick={() => speech.restartSpeaking()}
            disabled={speech.speaking || !speech.ready || isSubmitting}
            style={{
              backgroundColor: speech.listening ? "#2596be" : "lightgray",
              color: speech.listening ? "white" : "#242424",
            }}
          >
            Play Question Again
          </Button>
        )}
        {speech.listening ? (
          <Button
            shape="circular"
            appearance="outline"
            onClick={() => {
              speech.pauseSpeaking();
              speech.dispose();
            }}
            disabled={!speech.listening || !speech.ready}
          >
            Pause Interview
          </Button>
        ) : (
          <Button
            shape="circular"
            appearance="outline"
            onClick={() => speech.reinit()}
            disabled={
              speech.listening ||
              speech.speaking ||
              speech.ready ||
              speech.loading ||
              isSubmitting
            }
            style={{
              backgroundColor:
                speech.listening ||
                speech.speaking ||
                speech.ready ||
                speech.loading ||
                isSubmitting
                  ? "lightgray"
                  : "#2596be",
              color:
                speech.listening ||
                speech.speaking ||
                speech.ready ||
                speech.loading ||
                isSubmitting
                  ? "#242424"
                  : "white",
            }}
          >
            Resume Interview
          </Button>
        )}
      </div>
    </div>
  );
});

VoiceControls.displayName = "VoiceControls";
